import {
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  UntypedFormArray,
  AbstractControl,
  FormControl,
  ValidationErrors
} from '@angular/forms';
import { RegExps } from '../resources/regexps.constant';
import { BlacklistDomains } from '../utils/blacklist-domains';

export class CustomValidators {
  static mobile(control: UntypedFormControl) {
    const value = control.value;

    if (!value) {
      return null;
    }

    const firstNumber: number = Number(value.charAt(0));

    if (firstNumber !== 3 || value.length !== 10) {
      return { mobile: true };
    }

    const prefix: number = Number(value.slice(0, 3));
    const prefixAccepted: number[] = [
      300, 301, 302, 303, 304, 304, 305, 306, 310, 311, 312, 313, 314, 315, 316,
      317, 318, 319, 320, 321, 322, 323, 324, 333, 350, 351
    ];

    if (!prefixAccepted.includes(prefix)) {
      return { mobile: true };
    }

    const suffix: string = value.slice(3, 10);
    const firstDigit = suffix.charAt(0);

    if (suffix.split('').every((digit) => digit === firstDigit)) {
      return { mobile: true };
    }

    return null;
  }

  static groupEqual(options?: { insensitive: boolean }) {
    return (formGroup: UntypedFormGroup) => {
      const [firstControlName, ...otherControlNames] = Object.keys(
        formGroup.controls || {}
      );

      const isValid = otherControlNames.every((controlName) => {
        let valueCtrlName = formGroup.get(controlName).value;
        valueCtrlName =
          options?.insensitive && valueCtrlName
            ? (valueCtrlName as string).toLowerCase()
            : valueCtrlName;

        let firstValueCtrlName = formGroup.get(firstControlName).value;
        firstValueCtrlName =
          options?.insensitive && firstValueCtrlName
            ? (firstValueCtrlName as string).toLowerCase()
            : firstValueCtrlName;
        return valueCtrlName === firstValueCtrlName;
      });

      return isValid ? null : { groupNotEqual: true };
    };
  }

  static email(control: UntypedFormControl) {
    const value = control.value;

    if (!value) {
      return null;
    }

    const isEmail =
      RegExps.NEW_EMAIL.test(value) &&
      !RegExps.TWO_POINTS.test(value) &&
      !RegExps.FIRST_POINT.test(value) &&
      !RegExps.LAST_POINT.test(value);

    return isEmail ? null : { email: true };
  }

  static forbiddenMailDomains(control: UntypedFormControl) {
    if (!control.value) {
      return null;
    }

    const emailParts = control.value.split('@');
    if (emailParts.length === 2) {
      const domain = emailParts[1].toLowerCase();
      if (BlacklistDomains.includes(domain.toLowerCase())) {
        return { forbiddenDomain: { value: domain } };
      }
    }
    return null;
  }

  static landlinePhone(control: UntypedFormControl) {
    const value = control.value;

    if (!value) {
      return null;
    }

    if (value.length === 7) {
      const firstNumber: number = Number(value.charAt(0));
      if (
        firstNumber === 0 ||
        firstNumber === 1 ||
        !RegExps.REPEAT_NUMBER_2_TO_7.test(value)
      ) {
        return { landlineOrMobilePhone: true };
      }
    }
    return null;
  }

  static bannedWords(words: string[]): ValidatorFn {
    return (control: UntypedFormControl) => {
      let value = control.value as string;
      value = value
        ? value
            .normalize('NFD')
            .toLowerCase()
            .replace(RegExps.DIACRITICAL_MARKS, '')
        : value;
      return words.some((word) => (value ? value.includes(word) : false))
        ? { wordDetected: true }
        : null;
    };
  }

  static notAString(control: UntypedFormControl) {
    const value = control.value;
    return typeof value === 'string' ? { isString: true } : null;
  }

  static noWhitespaceValidator(control: FormControl) {
    return (control.value || '').trim().length ? null : { noWhiteSpace: true };
  }

  static noMoreThanThreeSameCharsExceptI(control: AbstractControl) {
    const valueControl = control.value;
    if (typeof valueControl !== 'string') {
      return null;
    }
    const regex = RegExps.NO_MORE_THAN_THREE_CHARS;
    const value = valueControl.toLowerCase();
    const isValid = !regex.test(value);

    return isValid ? null : { moreThanThreeSameChars: true };
  }

  static requiredCombo(control: UntypedFormControl) {
    const value = control.value;
    if (typeof value !== 'object') {
      return { required: true };
    }

    if (value === null) {
      return { required: true };
    }

    if (value === undefined) {
      return { required: true };
    }

    if (Object.keys(value).length === 0) {
      return { required: true };
    }
    return null;
  }

  static biggerThan(num: number): ValidatorFn {
    return (control: UntypedFormControl) => {
      let value = control.value;

      if (!value) {
        return null;
      }

      value = RegExps.NOT_NUMBERS.test(value)
        ? value.replace(RegExps.NOT_NUMBERS, '')
        : value;
      return value > num ? null : { notBiggerThan: true };
    };
  }

  static lesserThan(num: number): ValidatorFn {
    return (control: UntypedFormControl) => {
      let value = control.value;

      if (!value) {
        return null;
      }

      value = RegExps.NOT_NUMBERS.test(value)
        ? value.replace(RegExps.NOT_NUMBERS, '')
        : value;
      return value < num ? null : { notLesserThan: true };
    };
  }

  static groupOnlyOneNumber(formArray: UntypedFormArray) {
    const controls: string[] = Object.keys(formArray.controls || {});
    const isValid = controls.every((controlName) => {
      const value: string = formArray.get(controlName).value;
      return value && value.length === 1;
    });
    return isValid ? null : { groupDontOneNumber: true };
  }

  static passwordValidatorNonRepeatedNumber(control: AbstractControl) {
    let additionCount = 0;
    const array = control.value;
    const regEx = /\d/;
    const everyNumber = array.every((field: string) => {
      return regEx.test(field) && field.length;
    });

    if (everyNumber) {
      const repeat = array.reduce((count: any, num: any) => {
        count[num] = (count[num] || 0) + 1;
        return count;
      }, {});

      const repeatMaxValue = Math.max.apply(null, Object.values(repeat));

      array.forEach((element: any, i: any) => {
        const auxCount = array[i + 1] - array[i];
        const average =
          (parseInt(array[i], 10) + parseInt(array[i + 2], 10)) / 2 ===
          parseInt(array[i + 1], 10);
        if (average && auxCount === 1) {
          additionCount += 1;
        }
      });

      return repeatMaxValue > 2 || additionCount >= 1 ? { valid: true } : null;
    } else {
      return null;
    }
  }

  static groupEqualArray(formGroup: UntypedFormGroup) {
    const [firstControlName, ...otherControlNames] = Object.keys(
      formGroup.controls || {}
    );
    const isValid = otherControlNames.every(
      (controlName) =>
        JSON.stringify(formGroup.get(controlName).value) ===
        JSON.stringify(formGroup.get(firstControlName).value)
    );
    return isValid ? null : { groupNotEqualArray: true };
  }

  static notEmpty(control: UntypedFormControl) {
    const value = control.value;

    if (!value) {
      return null;
    }

    const valueTrim = (value as string).trim();

    return valueTrim.length > 0 ? null : { empty: true };
  }

  static groupNotEqual(formGroup: UntypedFormGroup) {
    const [firstControlName, ...otherControlNames] = Object.keys(
      formGroup.controls || {}
    );

    const isEqual = otherControlNames.every(
      (controlName) =>
        formGroup.get(controlName).value ===
        formGroup.get(firstControlName).value
    );

    return isEqual ? { groupEqual: true } : null;
  }

  static yearValidator(): ValidatorFn {
    return (control: UntypedFormControl): { [key: string]: any } | null => {
      const year = (+control.value).toString();
      if (year.length && year.length !== 4) {
        return { yearError: true };
      } else {
        return null;
      }
    };
  }

  static monthsDayValidation() {
    return (formGroup: UntypedFormGroup): { [key: string]: any } | null => {
      const dayInput = formGroup.get('day').value;
      const monthInput = formGroup.get('month').value;
      const yearInput = formGroup.get('year').value;

      if (
        yearInput?.length &&
        ((yearInput % 4 === 0 && yearInput % 100 !== 0) ||
          yearInput % 400 === 0)
      ) {
        // leap year
        if (monthInput && monthInput === 2 && dayInput > 29) {
          // February validation
          return { leapYear: true };
        } else if ([4, 6, 9, 11].includes(monthInput) && dayInput > 30) {
          // April, June, September, November validation
          return { monthsDaySecondError: true };
        }
      } else if (yearInput?.length) {
        // is not leap year
        if (monthInput && monthInput === 2 && dayInput > 28) {
          // February validation
          return { monthsDayFirstError: true };
        } else if ([4, 6, 9, 11].includes(monthInput) && dayInput > 30) {
          // April, June, September, November validation
          return { monthsDaySecondError: true };
        }
      }
      return null;
    };
  }

  static ageValidator() {
    return (formGroup: UntypedFormGroup): { [key: string]: any } | null => {
      const dayInput = formGroup.get('day').value;
      const monthInput = formGroup.get('month').value;
      const yearInput = formGroup.get('year').value;
      const date = new Date();
      const hour = date.getHours();
      const minute = date.getMinutes();

      if (yearInput?.length && monthInput && dayInput) {
        const controlValue = new Date(
          `${yearInput}/${monthInput}/${dayInput} ${hour}:${minute}:00 GMT-0500`
        );
        const getAge = Math.floor(
          (date.getTime() - new Date(controlValue).getTime()) / 3.15576e10
        );
        if (!isNaN(getAge)) {
          return getAge >= 18 && getAge <= 103 ? null : { rateAge: true };
        } else {
          return { rateAge: true };
        }
      } else {
        return null;
      }
    };
  }

  static futureDateValidator() {
    return (formGroup: UntypedFormGroup): { [key: string]: any } | null => {
      const dayInput = formGroup.get('day').value;
      const monthInput = formGroup.get('month').value;
      const yearInput = formGroup.get('year').value;
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);

      if (yearInput?.length === 4 && monthInput && dayInput) {
        const controlValue = new Date(`${yearInput}/${monthInput}/${dayInput}`);
        if (controlValue >= currentDate) {
          return { futureDate: true, rateAge: false };
        } else {
          return null;
        }
      } else {
        return null;
      }
    };
  }

  static expeditionDateValidator(
    flagValidateExpDate: boolean,
    expeditionDate?: Date
  ): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (flagValidateExpDate) {
        let validation: boolean = true;
        const birthControlValue = new Date(
          `${control.value.year}/${control.value.month}/${control.value.day}`
        );
        const yearModified = expeditionDate.getFullYear() - 18;
        const dateModified = new Date(
          yearModified,
          expeditionDate.getMonth(),
          expeditionDate.getDate()
        );

        validation = birthControlValue.getTime() > dateModified.getTime();
        return validation ? { expeditionDateValidationError: true } : null;
      } else {
        return null;
      }
    };
  }

  static idDateValidator(): ValidatorFn {
    return (control: UntypedFormControl): { [key: string]: any } | null => {
      const parent = control.parent;
      let idDateValidation: boolean = false;
      if (
        parent?.get('dateGroup')?.valid &&
        control.value.year &&
        control.value.month &&
        control.value.day
      ) {
        const parentDateGroup = control.parent.value.dateGroup;

        const firstInputDate = parentDateGroup.day;
        const secondInputDate = parentDateGroup.month;
        const thirdInputDate = parentDateGroup.year;
        const controlValue = new Date(
          `${thirdInputDate}/${secondInputDate}/${firstInputDate}`
        );
        const iputYearModified = controlValue.getFullYear() + 18;

        const firstInputModified = new Date(
          iputYearModified,
          controlValue.getMonth(),
          controlValue.getDate()
        );
        const secondControlValue = new Date(
          `${control.value.year}/${control.value.month}/${control.value.day}`
        );

        idDateValidation =
          firstInputModified.getTime() > secondControlValue.getTime();
      }
      return idDateValidation ? { idDateValidationError: true } : null;
    };
  }

  static dateValidator(control: UntypedFormControl) {
    const controlValue = new Date(control.value);
    const getAge = Math.floor(
      (new Date().getTime() - new Date(controlValue).getTime()) / 3.15576e10
    );
    if (!isNaN(getAge)) {
      return getAge >= 18 && getAge <= 113 ? null : { rateAge: true };
    } else {
      return { rateAge: true };
    }
  }

  static dateIdValidator(): ValidatorFn {
    return (control: UntypedFormControl): { [key: string]: any } | null => {
      const parent = control.parent;
      const firstInput = parent.get('birthdateInput').value;
      const firstIputYearModified = firstInput.getFullYear() + 17;

      const secondInput = control.value;
      let idDateValidation: boolean = false;
      if (firstInput && secondInput) {
        const firstInputModified = new Date(
          firstIputYearModified,
          firstInput.getMonth(),
          firstInput.getDate()
        );
        const secondControlValue = new Date(secondInput);

        idDateValidation =
          firstInputModified.getTime() > secondControlValue.getTime();
      }
      return idDateValidation ? { dateValidationError: true } : null;
    };
  }
  static identificationRangeValidator(control: UntypedFormControl) {
    const identification = parseInt(control.value, 10);

    if (identification) {
      const lessRange = identification >= 100 && identification <= 99999999;
      const greaterRange =
        identification >= 1000000000 && identification <= 2999999999;
      const greaterResult = greaterRange ? null : { rangeError: true };
      return lessRange ? null : greaterResult;
    } else {
      return undefined;
    }
  }

  static addressRulesValidator(
    control: UntypedFormControl
  ): ValidationErrors | null {
    const value = control.value as string;
    const [_firstWord, ...restParts] = value.split(/\s+/);
    const restOfString = ' ' + restParts.join(' ').trim();
    const notStartWith = 'sin';
    if (RegExps.NOT_MORE_THAN_TWO_LETTERS_EXCEPT_I.test(restOfString)) {
      return { tooManyRepeatedChars: true };
    }
    if (
      restOfString.length < 5 ||
      value.toLowerCase().startsWith(notStartWith)
    ) {
      return { lengthAfterFirstWordError: true };
    }
    if (value.length > 60) {
      return { maxLengthError: true };
    }
    return null;
  }

  static companyNameValidator(
    control: UntypedFormControl
  ): ValidationErrors | null {
    const value = control.value as string;
    if (!value) return null;
    return RegExp(RegExps.COMPANY_NAME_CHARACTERS).exec(value)
      ? { notAllowedCharactersError: true }
      : null;
  }
}
